// Ultimate Card Game (UCG) - Base Building System
// Six one-time base upgrades (Research Lab, Toxin Stack, EMP Array, Intel
// Uplink, Aegis Generator, Siege Battery). Drawn and played as normal Epic
// cards; a player may build at most 2 total, never the same one twice.

export const BUILDING_DEFS = {
  research_lab: {
    name: 'Research Lab',
    icon: '🔬',
    swapCooldown: 10 // seconds between free hand-card swaps
  },
  toxin_stack: {
    name: 'Toxin Stack',
    icon: '☣️',
    tickInterval: 8,
    cloudRadius: 70,
    cloudTravelTime: 6,
    slowMult: 0.6,
    slowDuration: 1500 // ms
  },
  emp_array: {
    name: 'EMP Array',
    icon: '📡',
    tickInterval: 8,
    slowMult: 0.65,
    slowDuration: 3000 // ms
  },
  intel_uplink: {
    name: 'Intel Uplink',
    icon: '🛰️'
  },
  aegis_generator: {
    name: 'Aegis Generator',
    icon: '🛡️',
    maxShields: 350,
    regenPerSec: 6
  },
  siege_battery: {
    name: 'Siege Battery',
    icon: '💥',
    range: 500,
    fireInterval: 20,
    damage: 90,
    splashRadius: 65
  }
};

export class BuildingSystem {
  constructor(battlefield, particles, audio) {
    this.battlefield = battlefield;
    this.particles = particles;
    this.audio = audio;
    this.maxBuildings = 2;
    this.reset();
  }

  reset() {
    this.built = { western: [], eastern: [] };
    this.state = { western: {}, eastern: {} };
    this.clouds = []; // traveling Toxin Stack gas clouds, both teams share one list
  }

  has(team, buildingId) {
    return this.built[team].includes(buildingId);
  }

  // Single source of truth for where a built attachment sits in canvas space -
  // the renderer, the Siege Battery's own firing origin, and the on-field
  // click hit-test all call this instead of recomputing the offset separately,
  // so the visual position and the interactive position can never drift apart.
  getAttachmentPosition(team, buildingId) {
    const slot = this.built[team].indexOf(buildingId);
    if (slot === -1) return null;
    const base = this.battlefield.bases[team];
    const dir = team === 'western' ? 1 : -1;
    return {
      x: base.x + dir * 26,
      y: base.y + (slot === 0 ? -92 : 92),
      dir
    };
  }

  canBuild(team, buildingId) {
    if (!BUILDING_DEFS[buildingId]) return false;
    if (this.has(team, buildingId)) return false;
    if (this.built[team].length >= this.maxBuildings) return false;
    return true;
  }

  build(team, buildingId) {
    if (!this.canBuild(team, buildingId)) return false;
    this.built[team].push(buildingId);

    if (buildingId === 'aegis_generator') {
      const base = this.battlefield.bases[team];
      base.maxShields = BUILDING_DEFS.aegis_generator.maxShields;
      base.shields = base.maxShields;
    }

    return true;
  }

  update(dt) {
    for (const team of ['western', 'eastern']) {
      if (this.built[team].length === 0) continue;
      if (this.has(team, 'research_lab')) this.tickResearchLab(team, dt);
      if (this.has(team, 'toxin_stack')) this.tickToxinStack(team, dt);
      if (this.has(team, 'emp_array')) this.tickEmpArray(team, dt);
      if (this.has(team, 'aegis_generator')) this.tickAegisGenerator(team, dt);
      if (this.has(team, 'siege_battery')) this.tickSiegeBattery(team, dt);
    }
    this.updateClouds(dt);
  }

  // ── Research Lab ─────────────────────────────────────────────────────
  tickResearchLab(team, dt) {
    const st = this.state[team];
    if (st.researchCooldown > 0) st.researchCooldown -= dt;
  }

  canFreeSwap(team) {
    return this.has(team, 'research_lab') && !(this.state[team].researchCooldown > 0);
  }

  freeSwapCooldownRatio(team) {
    const cd = this.state[team].researchCooldown || 0;
    return Math.max(0, Math.min(1, cd / BUILDING_DEFS.research_lab.swapCooldown));
  }

  triggerFreeSwapCooldown(team) {
    this.state[team].researchCooldown = BUILDING_DEFS.research_lab.swapCooldown;
  }

  // ── Aegis Generator: continuous shield regen on the base ─────────────
  tickAegisGenerator(team, dt) {
    const base = this.battlefield.bases[team];
    if (base.maxShields > 0 && base.shields < base.maxShields) {
      base.shields = Math.min(base.maxShields, base.shields + BUILDING_DEFS.aegis_generator.regenPerSec * dt);
    }
  }

  // ── EMP Array: instant global pulse vs enemy Mechanical units ────────
  tickEmpArray(team, dt) {
    const st = this.state[team];
    st.empTimer = (st.empTimer || 0) + dt;
    const def = BUILDING_DEFS.emp_array;
    if (st.empTimer < def.tickInterval) return;
    st.empTimer = 0;

    const enemyTeam = team === 'western' ? 'eastern' : 'western';
    let hitAny = false;
    for (const u of this.battlefield.units) {
      if (u.team !== enemyTeam || u.isDead) continue;
      if (!u.attributes?.includes('Mechanical')) continue;
      const existing = u.buffs.find(b => b.name === 'EMPSlow');
      if (existing) existing.duration = def.slowDuration;
      else u.buffs.push({ name: 'EMPSlow', speedMult: def.slowMult, duration: def.slowDuration });
      this.particles?.addElectricShock?.(u.x, u.y, u.radius * 1.6, '#38bdf8');
      hitAny = true;
    }
    if (hitAny) {
      this.audio?.playError?.();
      this.particles?.addScreenShake?.(1.5, 0.15);
    }
  }

  // ── Toxin Stack: periodically launches a drifting slow-cloud ─────────
  tickToxinStack(team, dt) {
    const st = this.state[team];
    st.toxinTimer = (st.toxinTimer || 0) + dt;
    const def = BUILDING_DEFS.toxin_stack;
    if (st.toxinTimer < def.tickInterval) return;
    st.toxinTimer = 0;

    const base = this.battlefield.bases[team];
    const enemyBase = this.battlefield.bases[team === 'western' ? 'eastern' : 'western'];
    const dir = team === 'western' ? 1 : -1;
    const travelDist = Math.abs(enemyBase.x - base.x);

    this.clouds.push({
      team,
      x: base.x + dir * 50,
      y: base.y,
      dir,
      speed: travelDist / def.cloudTravelTime,
      life: def.cloudTravelTime,
      radius: def.cloudRadius
    });
  }

  updateClouds(dt) {
    if (this.clouds.length === 0) return;
    const def = BUILDING_DEFS.toxin_stack;
    for (let i = this.clouds.length - 1; i >= 0; i--) {
      const c = this.clouds[i];
      c.x += c.dir * c.speed * dt;
      c.life -= dt;

      const enemyTeam = c.team === 'western' ? 'eastern' : 'western';
      for (const u of this.battlefield.units) {
        if (u.team !== enemyTeam || u.isDead) continue;
        if (!u.attributes?.includes('Biological')) continue;
        const dist = Math.sqrt((u.x - c.x)*(u.x - c.x)+(u.y - c.y)*(u.y - c.y));
        if (dist > c.radius + u.radius) continue;
        const existing = u.buffs.find(b => b.name === 'ToxinSlow');
        if (existing) existing.duration = def.slowDuration;
        else u.buffs.push({ name: 'ToxinSlow', speedMult: def.slowMult, duration: def.slowDuration });
      }

      if (Math.random() < 0.5) {
        this.particles?.addAcidSplash?.(c.x + (Math.random() * 24 - 12), c.y + (Math.random() * 24 - 12), 2);
      }

      if (c.life <= 0) this.clouds.splice(i, 1);
    }
  }

  // ── Siege Battery: independent second turret, longer range, slower ───
  tickSiegeBattery(team, dt) {
    const st = this.state[team];
    if (st.siegeCooldown === undefined) st.siegeCooldown = 0;
    if (st.siegeCooldown > 0) {
      st.siegeCooldown -= dt;
      return;
    }

    const def = BUILDING_DEFS.siege_battery;
    const base = this.battlefield.bases[team];
    const enemyTeam = team === 'western' ? 'eastern' : 'western';
    const dir = team === 'western' ? 1 : -1;

    // Targets the enemy unit that has pushed furthest into the player's own
    // territory within range - not simply the closest to the turret - so it
    // reads as "punishing overextension" rather than duplicating the base cannon.
    let target = null;
    let bestProgress = -Infinity;
    for (const u of this.battlefield.units) {
      if (u.team !== enemyTeam || u.isDead || u.isUntargetable || u.isGoldenStatue) continue;
      const dist = Math.sqrt((u.x - base.x)*(u.x - base.x)+(u.y - base.y)*(u.y - base.y));
      if (dist > def.range) continue;
      const progress = dir === 1 ? -u.x : u.x;
      if (progress > bestProgress) {
        bestProgress = progress;
        target = u;
      }
    }
    if (!target) return;

    st.siegeCooldown = def.fireInterval;
    // getAttachmentPosition() is also what the renderer draws the turret
    // sprite at, so the muzzle flash and shell always originate from wherever
    // Siege Battery actually appears on screen.
    const pos = this.getAttachmentPosition(team, 'siege_battery');
    const originX = pos ? pos.x : base.x + dir * 26;
    const originY = pos ? pos.y : base.y;

    this.audio?.playExplosion?.('large');
    this.particles?.addMuzzleFlash?.(originX, originY, '#ffaa00', 14);
    this.particles?.addScreenShake?.(3.0, 0.15);

    const dist = Math.hypot(target.x - originX, target.y - originY);
    const flightTimeSec = Math.max(0.20, Math.min(0.38, dist / 950));
    const flightTicks = Math.max(1, Math.round(flightTimeSec * 60));

    this.particles?.addBallisticBaseShell?.(originX, originY, target.x, target.y);
    this.battlefield.addProjectileSim({
      flightTicks,
      targetX: target.x,
      targetY: target.y,
      targetUnitId: target.id,
      damage: def.damage,
      splashRadius: def.splashRadius,
      team,
      projectileType: 'Grenade'
    });
  }
}
